<template>
  <Dialog :open="open" :title="dialogTitle" :maxWidth="440" showClose @close="$emit('close')">
    <div class="bp-create">
      <div class="bp-create__sub">{{ deviceName || 'Camera' }}</div>

      <div v-if="preArm" class="note">
        This camera's site is disarmed. It will be scheduled and bypassed <strong>Until Next Disarm</strong> the moment the system is armed.
      </div>

      <div class="label">Bypass duration</div>
      <div class="durations">
        <button
          v-for="d in durations"
          :key="d.key"
          class="duration"
          :class="{ 'duration--active': selectedKey === d.key }"
          @click="selectedKey = d.key">
          {{ d.label }}
        </button>
      </div>

      <div v-if="selectedKey === 'custom'" class="custom">
        <input
          type="number"
          inputmode="numeric"
          min="0"
          :max="maxHours"
          class="custom__input"
          v-model.number="customHours"
          placeholder="0" />
        <span class="custom__unit">h</span>
        <input
          type="number"
          inputmode="numeric"
          min="0"
          max="59"
          class="custom__input"
          v-model.number="customMinutes"
          placeholder="0" />
        <span class="custom__unit">min (max {{ maxHours }}h)</span>
      </div>

      <div class="preview">
        <template v-if="preArm">
          <ion-icon name="time-outline" />
          <span>Re-arms only when you disarm again</span>
        </template>
        <template v-else-if="isUntilDisarm">
          <ion-icon name="time-outline" />
          <span>Re-arms on next disarm</span>
        </template>
        <template v-else>
          <ion-icon name="time-outline" />
          <span>Re-arms in <strong>{{ rearmPreview }}</strong></span>
        </template>
      </div>
    </div>

    <template #actions>
      <button class="dlg-btn dlg-btn--ghost" @click="$emit('close')">Cancel</button>
      <button
        class="dlg-btn dlg-btn--confirm"
        :disabled="!canConfirm || submitting"
        @click="onConfirm">
        <ion-spinner v-if="submitting" name="crescent" />
        <span v-else>{{ confirmLabel }}</span>
      </button>
    </template>
  </Dialog>
</template>

<script>
import Dialog from '@/components/Dialog'
import {
  bypassDurationsForZone,
  bypassCustomMaxHours,
  resolveBypassDuration,
} from '@/models/bypass'

// Single-camera bypass creation, shown as a centered dialog (not a full modal)
// when "Bypass" is chosen on a camera. Driven app-wide from App.vue via the
// bypassCreateOpen / bypassCreateCtx store state.
export default {
  name: 'bypass-camera-create-dialog',
  components: { Dialog },
  props: {
    open: { type: Boolean, default: false },
    // { deviceId, siteId, deviceName, preArm }
    ctx: { type: Object, default: null },
    // Ionic modals don't reliably inject $store; allow it to be passed in.
    store: { type: Object, default: null },
  },
  data: function () {
    return {
      selectedKey: '1h',
      customHours: 1,
      customMinutes: 0,
      submitting: false,
    }
  },
  computed: {
    bpStore: function () {
      return this.store || this.$store
    },
    deviceId: function () {
      return (this.ctx && this.ctx.deviceId) || null
    },
    siteId: function () {
      return (this.ctx && this.ctx.siteId) || null
    },
    deviceName: function () {
      return (this.ctx && this.ctx.deviceName) || ''
    },
    preArm: function () {
      return !!(this.ctx && this.ctx.preArm)
    },
    // This camera's zone type drives the allowed durations: 24-hour zones cap at
    // 8h with no "until next disarm"; others cap at 24h. (siteDevice → zoneInfo.)
    zoneType: function () {
      const dev = this.deviceId ? this.bpStore.getters.siteDevice(this.deviceId) : null
      return dev && dev.zoneInfo ? dev.zoneInfo.zoneType : null
    },
    durations: function () {
      return bypassDurationsForZone(this.zoneType)
    },
    maxHours: function () {
      return bypassCustomMaxHours(this.zoneType)
    },
    // Edit of an already-active bypass (opened from the "Edit" action) vs a fresh
    // create. Routes onConfirm to editBypass (PUT) instead of bypassCamera (POST).
    isEdit: function () {
      return !!(this.ctx && this.ctx.edit)
    },
    dialogTitle: function () {
      if (this.preArm) return 'Pre-Arm Bypass'
      return this.isEdit ? 'Edit Bypass' : 'Bypass Camera'
    },
    confirmLabel: function () {
      if (this.preArm) return 'Schedule Pre-Arm Bypass'
      return this.isEdit ? 'Update Bypass' : 'Bypass Camera'
    },
    isUntilDisarm: function () {
      return this.selectedKey === 'untilDisarm'
    },
    customMs: function () {
      const h = Math.max(0, Number(this.customHours) || 0)
      const m = Math.max(0, Number(this.customMinutes) || 0)
      const ms = (h * 60 + m) * 60 * 1000
      const cap = this.maxHours * 60 * 60 * 1000
      return Math.min(cap, ms)
    },
    canConfirm: function () {
      if (this.preArm) return true
      if (this.selectedKey === 'custom') return this.customMs > 0
      return !!this.selectedKey
    },
    rearmPreview: function () {
      const now = this.bpStore.getters ? this.bpStore.getters.bypassNow : Date.now()
      const d = resolveBypassDuration(this.selectedKey, now, this.customMs)
      if (d.endTime == null) return 'Until Disarm'
      // Relative duration, never an absolute clock (see formatRemaining).
      return d.label
    },
  },
  watch: {
    // Reset the picker each time the dialog opens fresh.
    open: function (val) {
      if (!val) return
      this.selectedKey = this.preArm ? 'untilDisarm' : '1h'
      this.customHours = 1
      this.customMinutes = 0
      this.submitting = false
    },
    customHours: function () {
      this.clampCustom()
    },
    customMinutes: function () {
      this.clampCustom()
    },
  },
  methods: {
    // Keep custom entry within 0..maxHours and 0..59 min (max total = maxHours).
    // Empty strings are left alone so the field can be cleared while typing.
    clampCustom: function () {
      if (this.customHours !== '' && this.customHours != null) {
        let h = Math.floor(Number(this.customHours))
        if (!isFinite(h) || h < 0) h = 0
        if (h > this.maxHours) h = this.maxHours
        if (h !== this.customHours) this.customHours = h
      }
      if (this.customMinutes !== '' && this.customMinutes != null) {
        let m = Math.floor(Number(this.customMinutes))
        if (!isFinite(m) || m < 0) m = 0
        if (m > 59) m = 59
        if (m !== this.customMinutes) this.customMinutes = m
      }
      // At the hour cap, minutes must be 0 (can't exceed maxHours total).
      if (Number(this.customHours) >= this.maxHours && Number(this.customMinutes) > 0) {
        this.customMinutes = 0
      }
    },
    onConfirm: async function () {
      if (!this.canConfirm || this.submitting) return
      this.submitting = true
      try {
        // Editing an ACTIVE bypass must go through editBypass (PUT) — POSTing
        // create on an already-bypassed camera returns 409 bypass_already_active.
        // Pre-arm "edits" stay on bypassCamera: a pre-arm record is a local queue
        // (no server bypass exists yet), so PUT would have nothing to edit.
        if (this.isEdit && !this.preArm) {
          await this.bpStore.dispatch('editBypass', {
            deviceId: this.deviceId,
            durationKey: this.selectedKey,
            customMs: this.customMs,
          })
        } else {
          await this.bpStore.dispatch('bypassCamera', {
            deviceId: this.deviceId,
            siteId: this.siteId,
            durationKey: this.selectedKey,
            customMs: this.customMs,
            preArm: this.preArm,
          })
        }
        this.$emit('close')
      } catch (err) {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.bp-create__sub {
  font-size: 14px;
  opacity: 0.6;
  margin: -4px 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.note {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 18px;
  color: var(--chekt-bypass-7);
  background: rgba(245, 166, 35, 0.12);
}
.label {
  margin: 18px 0 10px;
  font-size: 13px;
  font-weight: 600;
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.durations {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.duration {
  padding: 12px 6px;
  border-radius: 10px;
  border: 1.5px solid var(--ion-color-dark-shade);
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: var(--ion-text-color);
  cursor: pointer;
  transition: all 0.15s ease;
}
.duration--active {
  border-color: var(--chekt-bypass);
  background: rgba(245, 166, 35, 0.14);
  color: var(--chekt-bypass-7);
}
.custom {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}
.custom__input {
  width: 72px;
  height: 40px;
  padding: 0 10px;
  border: 1.5px solid var(--ion-color-dark-shade, #c8c8c8);
  border-radius: 10px;
  background: transparent;
  color: var(--ion-text-color);
  font-size: 16px;
  text-align: center;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}
.custom__input:focus {
  border-color: var(--chekt-bypass);
}
.custom__unit {
  font-size: 13px;
  opacity: 0.6;
}
.preview {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  font-size: 14px;
  opacity: 0.85;
}
.preview ion-icon {
  color: var(--chekt-bypass);
  font-size: 18px;
}
.dlg-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 11px 0;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all 0.15s ease;
}
.dlg-btn[disabled] {
  opacity: 0.5;
  cursor: default;
}
.dlg-btn--ghost {
  background: transparent;
  border-color: rgba(0, 0, 0, 0.22);
  color: var(--ion-text-color);
}
.dlg-btn--confirm {
  background: var(--chekt-bypass);
  color: #ffffff;
}
.dlg-btn--confirm ion-spinner {
  width: 18px;
  height: 18px;
}
</style>
